import { useMsal } from '@azure/msal-react';
import { Button, DEFAULT_SCOPES } from '@city-sage/aurum-avis-labs-components';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@city-sage/aurum-avis-labs-components';
import { LogIn, UserPlus, Backpack, Heart, Camera, Users, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';

export interface LoginDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onLogin: () => void;
  onSignUp: () => void;
  title?: string;
  description?: string;
}

export function LoginDialog({ 
  isOpen, 
  onClose, 
  onLogin,
  onSignUp,
  title, 
  description,
}: LoginDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-full max-w-md mx-auto p-6">
        <DialogHeader className="text-center space-y-4 mb-6">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary text-primary-foreground">
            <Backpack className="h-8 w-8" />
          </div>
          <div className="space-y-2">
            <DialogTitle className="text-2xl font-bold text-center">
              {title || 'Authentication Required'}
            </DialogTitle>
            <DialogDescription className="text-muted-foreground text-center">
              {description || 'You need to be logged in to use this feature.'}
            </DialogDescription>
          </div>
        </DialogHeader>
        
        <div className="mb-6">
          <div className="rounded-lg bg-muted/50 p-4 border">
            <h3 className="font-semibold mb-4">Why join CitySage?</h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                  <Camera className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm">Create stunning travel stories with AI</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                  <Heart className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm">Like & save favorite tours</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                  <Users className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm">Connect with travelers</span>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 flex-shrink-0">
                  <MapPin className="h-4 w-4 text-primary" />
                </div>
                <span className="text-sm">Discover hidden gems</span>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-3">
          <Button 
            variant="default" 
            onClick={onSignUp}
            className="w-full"
            size="lg"
          >
            <UserPlus className="mr-2 h-4 w-4"/>
            Sign Up
          </Button>
          
          <Button 
            variant="outline" 
            onClick={onLogin}
            className="w-full"
            size="lg"
          >
            <LogIn className="mr-2 h-4 w-4"/>
            Log in
          </Button>
          
          <Button 
            variant="ghost" 
            onClick={onClose} 
            className="w-full"
          >
            Cancel
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 